package com.siriusxm.pia.views.chat

import androidx.compose.runtime.*
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.rememberCapability
import com.siriusxm.pia.components.*
import com.siriusxm.unifiedcontent.cmc.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.intellij.markdown.flavours.gfm.GFMFlavourDescriptor
import org.intellij.markdown.html.HtmlGenerator
import org.intellij.markdown.parser.MarkdownParser
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.time.Clock

/**
 * Main chat conversation view.
 *
 * Renders a two-panel layout:
 * - Left sidebar: recent conversation history (listed via GET /chat)
 * - Right panel: active conversation with message bubbles, input, and async polling
 *
 * The URL is the single source of truth for which conversation is on screen. [conversationId] comes
 * from the route, and one effect keyed on it owns loading and polling that conversation, so a switch
 * (sidebar click, pasted URL, or browser back/forward) cancels whatever the previous conversation
 * was still doing before anything for the new one is displayed.
 *
 * After sending a message the view immediately shows the user turn and polls
 * GET /chat/{conversationId} every second until status is COMPLETED or FAILED.
 *
 * @param app The ChatApplication instance providing the CMC service client
 * @param conversationId The conversation from the URL, or null for a new, unsent conversation
 */
@Composable
fun ConversationView(app: ChatApplication, conversationId: String?) {
    var userInput by remember { mutableStateOf("") }
    var displayMessages by remember { mutableStateOf<List<ChatMessage>>(emptyList()) }
    var isProcessing by remember { mutableStateOf(false) }
    var isLoadingConversation by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var statusMessage by remember { mutableStateOf("Thinking...") }
    var recentConversations by remember { mutableStateOf<List<ChatListItem>>(emptyList()) }
    var isLoadingHistory by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()

    // Whether the current user may open the transcript admin UI (UCA-1091). Resolved from AVP; false
    // until known and false on any failure (fail closed), so the affordance only ever appears for
    // users the server would also allow. This is a UI hint, not the authorization boundary.
    val canViewTranscripts by app.context.rememberCapability(ViewChatTranscripts)

    // The id this view navigated to itself after starting a turn. The messages on screen already
    // belong to that conversation (the user's turn was appended optimistically before the request),
    // so the load effect must keep them; every other change of id is a switch to a different
    // conversation, whose messages replace what is on screen.
    var selfNavigatedId by remember { mutableStateOf<String?>(null) }

    // Bumped once per turn taken on the conversation already on screen. Continuing a conversation
    // returns the same id, so `navigate("chat/$id")` is a no-op and the id-keyed load/poll effect
    // never re-fires on its own. This counter is a second key on that effect: incrementing it is what
    // makes a same-conversation turn resume polling.
    var turnNonce by remember { mutableStateOf(0) }

    // Sidebar history, loaded once for the life of the view.
    LaunchedEffect(Unit) {
        try {
            val result = app.cmcService.chat.listChats(limit = 20)
            recentConversations = result.conversations
        } catch (e: Exception) {
            console.log("Failed to load conversation history: ${e.message}")
        } finally {
            isLoadingHistory = false
        }
    }

    // Owns whichever conversation the URL points at: load its messages, then resume polling if the
    // turn is still running server-side.
    //
    // Keying on the id is what makes switching safe. Changing conversations cancels this coroutine,
    // so a load or a poll still in flight for the previous conversation cannot write its result into
    // the conversation now on screen. Without that, a completing poll ran
    // `displayMessages + response` against the *new* conversation's list, which is why switching
    // chats looked like the new one being appended to the old one.
    LaunchedEffect(conversationId, turnNonce) {
        val switched = conversationId != selfNavigatedId
        // The marker is consumed on use: coming back to this same conversation later is an ordinary
        // switch, and must clear the screen like any other.
        selfNavigatedId = null

        if (switched) {
            // Drop the previous conversation's state up front, so nothing from it is visible while
            // the new one loads.
            displayMessages = emptyList()
            isProcessing = false
            errorMessage = null
            statusMessage = "Resuming..."
        }
        if (conversationId == null) return@LaunchedEffect

        isLoadingConversation = switched
        val conversation = try {
            fetchConversation(conversationId, app)
        } finally {
            isLoadingConversation = false
        }

        // Only rebuild the on-screen messages from the fetched record on a real switch. On a
        // self-started turn (new conversation or a continuation) the record is eventually consistent:
        // a stale read can briefly return the PREVIOUS turn's COMPLETED response, and rebuilding from
        // that would clobber the optimistic messages already on screen and — being terminal —
        // suppress polling for the turn that is actually running.
        if (switched && conversation != null) {
            displayMessages = buildList {
                addAll(conversation.messages ?: emptyList())
                if (conversation.status == ChatStatus.COMPLETED && conversation.response != null) {
                    add(ChatMessage(role = "assistant", content = conversation.response))
                }
            }
        }

        val shouldPoll = if (!switched) {
            // We just started or continued a turn here; it is running server-side regardless of what
            // this (possibly stale) read saw, so always poll for its result.
            true
        } else when (conversation?.status) {
            ChatStatus.PENDING, ChatStatus.PROCESSING -> true
            // The read failed for a conversation the user switched to: nothing in flight to wait for.
            else -> false
        }

        // Only surface a terminal error from the fetched record on a real switch, for the same
        // eventual-consistency reason as the message rebuild above: on a self-started turn a stale
        // read could carry the previous turn's FAILED status and raise a spurious banner over a turn
        // that is in fact running.
        if (switched) {
            if (conversation?.status == ChatStatus.FAILED) {
                errorMessage = conversation.error ?: "The assistant failed to respond."
            } else if (conversation == null) {
                errorMessage = "Could not load this conversation."
            }
        }

        if (!shouldPoll) {
            // Nothing outstanding: clear the spinner the send handler (or a previous turn) left on.
            isProcessing = false
            return@LaunchedEffect
        }

        isProcessing = true
        pollForResult(conversationId, app,
            onStatusUpdate = { msg -> statusMessage = msg },
            onComplete = { response ->
                displayMessages = displayMessages + ChatMessage(role = "assistant", content = response)
                isProcessing = false
            },
            onError = { err ->
                errorMessage = err
                isProcessing = false
            }
        )
        // The turn has reached a terminal state, so the sidebar is stale: a conversation started from
        // this view is missing from the list entirely, and a resumed one still shows as in-progress.
        // A cancelled poll never gets here, having nothing to report.
        try {
            recentConversations = app.cmcService.chat.listChats(limit = 20).conversations
        } catch (e: Exception) {
            console.log("Failed to refresh conversation history: ${e.message}")
        }
    }

    // Page layout: sidebar + main panel.
    //
    // `ApplicationView` mounts each application as an item of a flex *row*, so without an explicit
    // grow factor this container is sized to its own content and stops short of the window edge.
    // `flex(1, 1)` claims the full row width; `minWidth(0)` overrides the automatic minimum so a
    // wide message (a long code block, say) shrinks the panel's scroll area instead of stretching
    // the panel past the viewport.
    // Rules for the markdown HTML injected into assistant bubbles, which cannot be styled inline.
    Style(ChatStyles)

    Div({
        style {
            display(DisplayStyle.Flex)
            flex(1, 1)
            minWidth(0.px)
            height(100.percent)
            overflow("hidden")
        }
    }) {
        // Sidebar: conversation history. Selecting only navigates — the effect keyed on the URL's
        // conversation id does the loading, so there is exactly one place that can write messages.
        ConversationSidebar(
            conversations = recentConversations,
            selectedId = conversationId,
            isLoading = isLoadingHistory,
            showTranscripts = canViewTranscripts,
            onViewTranscripts = { app.context.navigate("chat/transcripts") },
            onSelect = { id ->
                if (id != conversationId) {
                    app.context.navigate("chat/$id")
                }
            },
            onNewChat = {
                // Clears as well as navigates: when the URL is already `/chat` (a first message
                // still in flight) the navigation is a no-op and the load effect will not re-run,
                // so the reset has to happen here.
                displayMessages = emptyList()
                isProcessing = false
                errorMessage = null
                app.context.navigate("chat")
            }
        )

        // Main chat panel. `minWidth(0)` for the same reason as the container above: the panel must
        // be allowed to shrink below its content's intrinsic width, or unbreakable message content
        // widens it and pushes the input area off-screen.
        Div({
            style {
                flex(1)
                minWidth(0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                height(100.percent)
                overflow("hidden")
            }
        }) {
            // Messages area
            Div({
                style {
                    flex(1)
                    overflowY("auto")
                    padding(24.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(16.px)
                }
            }) {
                if (isLoadingConversation) {
                    ProcessingIndicator("Loading conversation...")
                } else if (displayMessages.isEmpty() && !isProcessing) {
                    ChatEmptyState()
                }

                // Keyed on the conversation as well as the position: without a key, Compose reuses
                // bubble nodes positionally across conversations, and a reused assistant bubble
                // keeps the DOM subtree its `ref` populated when it was created (see
                // MarkdownContent). Keying forces fresh nodes when the conversation changes.
                displayMessages.forEachIndexed { index, msg ->
                    key(conversationId, index) {
                        MessageBubble(msg)
                    }
                }

                if (isProcessing) {
                    ProcessingIndicator(statusMessage)
                }

                errorMessage?.let { err ->
                    ErrorMessage(err) { errorMessage = null }
                }
            }

            // Input area
            ChatInputArea(
                value = userInput,
                isProcessing = isProcessing,
                onValueChange = { userInput = it },
                onSend = {
                    if (userInput.isNotBlank() && !isProcessing) {
                        val text = userInput.trim()
                        userInput = ""
                        errorMessage = null
                        statusMessage = "Thinking..."

                        val userMsg = ChatMessage(role = "user", content = text)
                        displayMessages = displayMessages + userMsg
                        isProcessing = true

                        // Send the turn with the current conversation id so the server appends to
                        // the same record (one sidebar row per conversation) instead of creating a
                        // new one. `selfNavigatedId` tells the load effect this is a continuation, not
                        // a switch, so the optimistic messages already on screen survive. A brand-new
                        // conversation navigates to its returned id; a continuation keeps the same id
                        // (so navigation is a no-op) and bumps `turnNonce` to re-fire the effect and
                        // resume polling.
                        scope.launch {
                            try {
                                val allMessages = displayMessages.map {
                                    ChatMessage(role = it.role, content = it.content)
                                }
                                val result = app.cmcService.chat.startChat(
                                    StartChatPayload(
                                        messages = allMessages,
                                        systemPrompt = null,
                                        conversationId = conversationId
                                    )
                                )
                                val convId = result.conversationId
                                selfNavigatedId = convId
                                if (convId == conversationId) {
                                    turnNonce++
                                } else {
                                    app.context.navigate("chat/$convId")
                                }
                            } catch (e: CancellationException) {
                                throw e
                            } catch (e: Exception) {
                                errorMessage = "Failed to send message: ${e.message}"
                                isProcessing = false
                            }
                        }
                    }
                }
            )
        }
    }
}

/**
 * Polls GET /chat/{id} until completed or failed.
 *
 * [CancellationException] is rethrown ahead of the general handler: cancellation is how switching
 * conversations stops this poll, and reporting it through [onError] would raise a "Polling error"
 * banner over the conversation the user just switched to.
 */
private suspend fun pollForResult(
    conversationId: String,
    app: ChatApplication,
    onStatusUpdate: (String) -> Unit,
    onComplete: (String) -> Unit,
    onError: (String) -> Unit
) {
    val maxAttempts = 180
    repeat(maxAttempts) { attempt ->
        try {
            val conv = app.cmcService.chat.getChat(conversationId)
            when (conv.status) {
                ChatStatus.COMPLETED -> {
                    onComplete(conv.response ?: "No response")
                    return
                }
                ChatStatus.FAILED -> {
                    onError(conv.error ?: "Unknown error")
                    return
                }
                else -> {
                    conv.statusMessage?.let { onStatusUpdate(it) }
                    delay(1000)
                }
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            onError("Polling error: ${e.message}")
            return
        }
    }
    onError("Timeout waiting for response")
}

/**
 * Reads a conversation by id, returning null if it could not be read.
 *
 * As in [pollForResult], cancellation is rethrown rather than reported as a load failure: the caller
 * distinguishes "this conversation is unreadable" from "you switched away from it".
 */
private suspend fun fetchConversation(conversationId: String, app: ChatApplication) = try {
    app.cmcService.chat.getChat(conversationId)
} catch (e: CancellationException) {
    throw e
} catch (e: Exception) {
    console.log("Failed to load conversation $conversationId: ${e.message}")
    null
}

/**
 * Left sidebar showing the conversation history list.
 *
 * When [showTranscripts] is true a footer link to the admin transcript review UI is rendered; it is
 * driven by the caller's AVP capability check, so the link only appears for users the server would
 * also authorize. The link is a UI affordance — the transcript API is enforced independently.
 */
@Composable
private fun ConversationSidebar(
    conversations: List<ChatListItem>,
    selectedId: String?,
    isLoading: Boolean,
    showTranscripts: Boolean,
    onViewTranscripts: () -> Unit,
    onSelect: (String) -> Unit,
    onNewChat: () -> Unit
) {
    Div({
        style {
            width(260.px)
            flexShrink(0)
            property("border-right", "1px solid ${SXMUI.borderDividerDefault.value()}")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            overflow("hidden")
            backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
        }
    }) {
        // Header + new chat button
        Div({
            style {
                padding(16.px)
                property("border-bottom", "1px solid ${SXMUI.borderDividerDefault.value()}")
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Span({
                style {
                    fontWeight(700)
                    fontSize(14.px)
                    color(SXMUI.textColor.value())
                }
            }) { Text("History") }

            Button({
                style {
                    padding(4.px, 10.px)
                    borderRadius(4.px)
                    border(0.px)
                    backgroundColor(SXMUI.buttonBackgroundPrimary.value())
                    color(SXMUI.buttonForegroundPrimary.value())
                    cursor("pointer")
                    fontSize(12.px)
                }
                onClick { onNewChat() }
            }) { Text("New") }
        }

        // List
        Div({ style { flex(1); overflowY("auto") } }) {
            if (isLoading) {
                Div({ style { padding(16.px); color(SXMUI.subtleTextColor.value()); fontSize(13.px) } }) {
                    Text("Loading...")
                }
            } else if (conversations.isEmpty()) {
                Div({ style { padding(16.px); color(SXMUI.subtleTextColor.value()); fontSize(13.px) } }) {
                    Text("No conversations yet")
                }
            } else {
                conversations.forEach { conv ->
                    ConversationListItem(
                        conversation = conv,
                        isSelected = conv.conversationId == selectedId,
                        onClick = { onSelect(conv.conversationId) }
                    )
                }
            }
        }

        // Admin footer: transcript review, shown only when AVP allows this user (UCA-1091).
        if (showTranscripts) {
            Div({
                style {
                    padding(12.px, 14.px)
                    property("border-top", "1px solid ${SXMUI.borderDividerDefault.value()}")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(6.px)
                    cursor("pointer")
                    color(SXMUI.subtleTextColor.value())
                    fontSize(12.px)
                }
                onClick { onViewTranscripts() }
            }) {
                svgIcon(SVGIcons.tool) { size = 14.px }
                Span { Text("Transcripts") }
            }
        }
    }
}

/**
 * A single row in the conversation history sidebar.
 */
@Composable
fun ConversationListItem(conversation: ChatListItem, isSelected: Boolean, onClick: () -> Unit) {
    val statusColor = when (conversation.status) {
        ChatStatus.COMPLETED -> SXMUI.statusSuccessColor.value()
        ChatStatus.FAILED -> SXMUI.statusErrorColor.value()
        else -> SXMUI.statusPendingColor.value()
    }

    Div({
        style {
            padding(10.px, 14.px)
            cursor("pointer")
            property("border-bottom", "1px solid ${SXMUI.borderDividerDefault.value()}")
            if (isSelected) backgroundColor(SXMUI.borderDividerDefault.value())
        }
        onClick { onClick() }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(4.px)
            }
        }) {
            Span({
                style {
                    width(8.px); height(8.px)
                    borderRadius(50.percent)
                    backgroundColor(statusColor)
                    marginTop(4.px)
                    display(DisplayStyle.InlineBlock)
                    flexShrink(0)
                }
            }) {}
        }

        Div({
            style {
                fontSize(12.px)
                color(SXMUI.textColor.value())
                overflow("hidden")
                property("text-overflow", "ellipsis")
                property("white-space", "nowrap")
            }
        }) {
            Text(conversation.preview ?: conversation.conversationId.take(8) + "...")
        }
    }
}

/**
 * A single message bubble in the conversation. User messages appear on the right
 * in brand color; assistant responses appear on the left and are rendered as markdown.
 */
@Composable
fun MessageBubble(message: ChatMessage) {
    val isUser = message.role == "user"
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(if (isUser) JustifyContent.FlexEnd else JustifyContent.FlexStart)
        }
    }) {
        Div({
            style {
                padding(10.px, 14.px)
                borderRadius(12.px)
                maxWidth(75.percent)
                if (isUser) {
                    backgroundColor(SXMUI.buttonBackgroundPrimary.value())
                    color(SXMUI.buttonForegroundPrimary.value())
                } else {
                    backgroundColor(SXMUI.containerContentBackgroundColor.value())
                    color(SXMUI.textColor.value())
                    border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
                }
            }
        }) {
            if (isUser) {
                Text(message.content ?: "")
            } else {
                MarkdownContent(message.content ?: "")
            }
        }
    }
}

/**
 * Renders assistant markdown responses as HTML.
 * Strips &lt;thinking&gt; blocks before rendering and sanitizes the resulting HTML
 * to prevent XSS via event-handler attributes or script tags in model output.
 *
 * Parsed as GitHub Flavored Markdown, not CommonMark: tables, strikethrough, task lists and
 * autolinks are all GFM extensions, and models write tables freely. Under CommonMark a table is not
 * a construct at all, so its rows come out as literal pipe-delimited paragraph text. Presentation
 * for the generated elements lives in [ChatStyles].
 *
 * The content is wrapped in [key] because `ref` only runs when its element is created, never on
 * recomposition. Recomposing this composable with different markdown would otherwise leave the
 * previous render's HTML in the DOM — visible when Compose reuses a bubble for a different message,
 * as it does when the conversation changes. Keying on the markdown discards the element instead, so
 * the new `ref` sets the new HTML. Parsing is memoised on the same key.
 */
@Composable
fun MarkdownContent(markdown: String) {
    val safe = remember(markdown) {
        val cleaned = markdown.replace(Regex("<thinking>[\\s\\S]*?</thinking>\\s*"), "").trim()
        val flavour = GFMFlavourDescriptor()
        val tree = MarkdownParser(flavour).buildMarkdownTreeFromString(cleaned)
        sanitizeHtml(HtmlGenerator(cleaned, tree, flavour).generateHtml())
    }

    key(markdown) {
        Div({
            classes("chat-markdown")
            ref { element ->
                element.innerHTML = safe
                onDispose {}
            }
        })
    }
}

/**
 * Strips event-handler attributes, script tags, and javascript: URIs from HTML
 * to prevent XSS when setting innerHTML with model-generated content.
 *
 * Case insensitivity comes from [RegexOption.IGNORE_CASE] rather than an inline `(?i)` flag. This
 * code targets JS, where Kotlin's [Regex] is a JavaScript `RegExp`, and JavaScript has no inline
 * flag groups — `(?i)` is read as a group named `?i` and throws `Invalid regular expression:
 * Invalid group` when the pattern is constructed. Being a runtime failure it compiles cleanly, and
 * on the JVM the inline form works, so a JVM test would not catch a regression here.
 */
private fun sanitizeHtml(html: String): String =
    html
        .replace(Regex("""\s+on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]*)""", RegexOption.IGNORE_CASE), "")
        .replace(Regex("""<script[\s\S]*?</script\s*>""", RegexOption.IGNORE_CASE), "")
        .replace(Regex("""(href|src)\s*=\s*["']?\s*javascript\s*:""", RegexOption.IGNORE_CASE), "href=\"#\"")

/**
 * The input area at the bottom of the chat panel, with a multi-line textarea and send button.
 */
@Composable
private fun ChatInputArea(
    value: String,
    isProcessing: Boolean,
    onValueChange: (String) -> Unit,
    onSend: () -> Unit
) {
    Div({
        style {
            padding(16.px)
            borderTop(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(8.px)
                alignItems(AlignItems.FlexEnd)
            }
        }) {
            TextArea(value) {
                style {
                    flex(1)
                    property("resize", "vertical")
                    minHeight(44.px)
                    maxHeight(200.px)
                    padding(10.px, 12.px)
                    border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
                    borderRadius(8.px)
                    fontSize(14.px)
                    color(SXMUI.textColor.value())
                    backgroundColor(SXMUI.mainLayoutBackgroundColor.value())
                    property("outline", "none")
                    property("font-family", "inherit")
                }
                placeholder("Ask anything about SiriusXM content...")
                if (isProcessing) disabled()
                onInput { onValueChange(it.value) }
                onKeyDown { event ->
                    if (event.key == "Enter" && !event.shiftKey) {
                        event.preventDefault()
                        onSend()
                    }
                }
            }

            Button({
                style {
                    padding(10.px, 16.px)
                    borderRadius(8.px)
                    border(0.px)
                    backgroundColor(if (value.isNotBlank() && !isProcessing) SXMUI.buttonBackgroundPrimary.value() else SXMUI.borderDividerDefault.value())
                    color(if (value.isNotBlank() && !isProcessing) SXMUI.buttonForegroundPrimary.value() else SXMUI.subtleTextColor.value())
                    cursor(if (value.isNotBlank() && !isProcessing) "pointer" else "not-allowed")
                    height(44.px)
                }
                if (isProcessing || value.isBlank()) disabled()
                onClick { onSend() }
            }) {
                svgIcon(SVGIcons.arrowUp) { size = 18.px }
            }
        }

        Div({
            style {
                marginTop(6.px)
                fontSize(11.px)
                color(SXMUI.subtleTextColor.value())
            }
        }) {
            Text("Enter to send · Shift+Enter for new line")
        }
    }
}

/** Shown when there are no messages yet. */
@Composable
private fun ChatEmptyState() {
    Div({
        style {
            flex(1)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            color(SXMUI.subtleTextColor.value())
            gap(8.px)
            padding(48.px)
        }
    }) {
        svgIcon(SVGIcons.messageCircle) { size = 48.px }
        Div({ style { fontSize(16.px); fontWeight(600) } }) { Text("Ask about SiriusXM content") }
        Div({ style { fontSize(13.px); property("text-align", "center") } }) {
            Text("Search shows, channels, episodes, and more using natural language.")
        }
    }
}

/** Processing spinner shown while the LLM is working. */
@Composable
private fun ProcessingIndicator(statusMessage: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            padding(10.px, 14.px)
            borderRadius(12.px)
            backgroundColor(SXMUI.containerContentBackgroundColor.value())
            border(1.px, LineStyle.Solid, SXMUI.borderDividerDefault.value())
            maxWidth(75.percent)
            color(SXMUI.subtleTextColor.value())
            fontSize(13.px)
        }
    }) {
        Span({ style { property("animation", "spin 1s linear infinite") } }) { Text("⋯") }
        Text(statusMessage)
    }
}

/** Dismissible error banner. */
@Composable
private fun ErrorMessage(message: String, onDismiss: () -> Unit) {
    Div({
        style {
            padding(10.px, 14.px)
            borderRadius(8.px)
            backgroundColor(Color("#fff3f3"))
            border(1.px, LineStyle.Solid, Color("#f5c6cb"))
            color(Color("#721c24"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            fontSize(13.px)
        }
    }) {
        Text(message)
        Span({
            style { cursor("pointer"); marginLeft(8.px) }
            onClick { onDismiss() }
        }) { Text("×") }
    }
}
